import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { useTrips } from "@/hooks/useTrips";
import { useExpenses } from "@/hooks/useExpenses";
import { format, parseISO } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { getTripInstallments } from "@/lib/tripPayoutSchedule";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

export const MonthlyEarningsChart = () => {
  const { trips, isLoading } = useTrips();

  const monthlyEarnings = useMemo(() => {
    if (!trips || trips.length === 0) return [];

    const byMonth: Record<string, { settled: number; upcoming: number }> = {};

    trips.forEach(trip => {
      const installments = getTripInstallments(trip);
      installments.forEach(({ date, amount, settled }) => {
        const monthKey = format(date, "yyyy-MM");
        if (!byMonth[monthKey]) byMonth[monthKey] = { settled: 0, upcoming: 0 };
        if (settled) byMonth[monthKey].settled += amount;
        else byMonth[monthKey].upcoming += amount;
      });
    });

    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, v]) => ({
        month: format(parseISO(month + "-01"), "MMM yyyy"),
        settled: Math.round(v.settled * 100) / 100,
        upcoming: Math.round(v.upcoming * 100) / 100,
        total: Math.round((v.settled + v.upcoming) * 100) / 100,
      }));
  }, [trips]);

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Monthly Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const hasUpcoming = monthlyEarnings.some((m) => m.upcoming > 0);

  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold text-foreground">Monthly Earnings</CardTitle>
          {hasUpcoming && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: "hsl(var(--primary))" }} />
                Settled
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: "hsl(var(--primary) / 0.35)" }} />
                Upcoming
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {monthlyEarnings.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyEarnings}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="month"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
              />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload as { settled: number; upcoming: number; total: number };
                  return (
                    <div style={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "var(--radius)",
                      color: "hsl(var(--popover-foreground))",
                      padding: "8px 12px",
                      fontSize: 13,
                    }}>
                      <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
                      <p>Settled: {formatCurrency(d.settled)}</p>
                      {d.upcoming > 0 && <p>Upcoming: {formatCurrency(d.upcoming)}</p>}
                      <p style={{ fontWeight: 600, marginTop: 4 }}>Total: {formatCurrency(d.total)}</p>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="settled"
                stackId="earnings"
                fill="hsl(var(--primary))"
              />
              <Bar
                dataKey="upcoming"
                stackId="earnings"
                fill="hsl(var(--primary) / 0.35)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No earnings data yet. Import trips from the Earnings tab.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const MonthlyExpensesChart = () => {
  const { expenses, isLoading } = useExpenses();

  const monthlyExpenses = useMemo(() => {
    if (!expenses || expenses.length === 0) return [];
    
    const expensesByMonth: Record<string, number> = {};
    
    expenses.forEach(expense => {
      if (expense.expense_date && expense.amount != null) {
        try {
          const date = parseISO(expense.expense_date);
          const monthKey = format(date, "yyyy-MM");
          expensesByMonth[monthKey] = (expensesByMonth[monthKey] || 0) + expense.amount;
        } catch {
          // Skip invalid dates
        }
      }
    });
    
    return Object.entries(expensesByMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({
        month: format(parseISO(month + "-01"), "MMM yyyy"),
        expenses: Math.round(amount * 100) / 100
      }));
  }, [expenses]);

  if (isLoading) {
    return (
      <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Monthly Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Monthly Expenses</CardTitle>
      </CardHeader>
      <CardContent>
        {monthlyExpenses.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyExpenses}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "var(--radius)",
                  color: "hsl(var(--popover-foreground))"
                }}
                formatter={(value: number) => [formatCurrency(value), "Expenses"]}
              />
              <Bar 
                dataKey="expenses" 
                fill="hsl(var(--destructive))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No expense data yet. Add expenses from the Expenses tab.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
